/**
@format
@flow
*/
import React, { useState } from 'react';
import styled from 'styled-components';
import ListItem from '@ziynx/components/listItems';
import Link from '@ziynx/components/links';
import type { ListItem as ListItemType } from '@ziynx/flow-types/listItems';
import { getDimensions } from '@ziynx/components/utilities';

export type Props = $ReadOnly<{|
  /**
   * Content array of object passed
   */
  content?: Array<ListItemType>,
  /**
   * Small or Medium List Variants
   */
  size?: 'small' | 'medium',
  /**
   * If listSquircle is collapsed
   */
  isCollapsed?: boolean,
  /**
   * Width for list
   * Default to inherit from parent
   */
  width?: string | number,
  /**
   * Max-width for list, optional
   * Default to inherit from parent
   */
  maxWidth?: string,

  renderListItem?: () => React$Node,

|}>;

const StyledList = styled.ul`
  padding: 0;
  width: ${({ width }) => getDimensions(width)};
  ${({ maxWidth }) => maxWidth && `max-width: ${getDimensions(maxWidth)};`};
  ${({ isCollapsed }) => isCollapsed && 'transition: width 400ms; width: 68px;'};
`;

const textAlignItems = (text, label) => {
  if (typeof text === 'string' && !label) {
    return 'center';
  }
  return 'flex-start';
};

const StyledListItem = styled(ListItem)`
  margin: ${({ size }) => (size === 'medium' ? '24px 0px' : '0')};
  align-items: ${({ text, label }) => textAlignItems(text, label)};
`;

const ListSquircle = (props: Props): React$Node => {
  const {
    content, width, maxWidth, isCollapsed, size, renderListItem, ...rest
  } = props;
  const [activeIndex, setActiveIndex] = useState(0);

  let contentFormat;
  if (size === 'medium' && Array.isArray(content)) {
    contentFormat = content.slice(0, 3);
  } else {
    contentFormat = content;
  }

  // eslint-disable-next-line max-len
  const renderDefaultListItem = (): React$Node => {
    if (!contentFormat) return null;
    return contentFormat.map((item: Object, index: number) => {
      const keyIndex = `${item?.text}${index}`;

      const handleIsActive = (match: Object): void => {
        if (match?.isExact && !item?.disabled) setActiveIndex(index);
      };

      return (
        <Link
          variant="inline"
          activeOnlyWhenExact
          key={keyIndex}
          to={item?.path}
          isActive={handleIsActive}
        >
          <StyledListItem
            variant="squircle"
            key={keyIndex}
            size={size}
            isCollapsed={isCollapsed}
            isSelected={index === activeIndex}
            disabled={item?.disabled}
            label={size === 'medium' ? item?.label : null}
            text={size === 'medium' ? item?.text : item?.label?.text}
            iconName={item?.iconName}
            imageSource={item?.imageSource}
            imageAltText={item?.imageAltText}
            squircleText={item?.squircleText}
            maxWidth="100%"
          />
        </Link>
      );
    });
  };

  return (
    <StyledList isCollapsed={isCollapsed} width={width} maxWidth={maxWidth} {...rest}>
      {renderListItem ? renderListItem() : renderDefaultListItem()}
    </StyledList>
  );
};

ListSquircle.defaultProps = {
  size: 'small',
};

export default ListSquircle;
